import type { AlertColor } from '@mui/material';
import { noop } from 'lodash';
import React, { useCallback, useState } from 'react';

import AppSnackbar from '../components/AppSnackbar';

interface SnackbarInfo {
	type: AlertColor;
	message: string;
}

interface Context {
	isOpen: boolean;
	closeSnackbar: () => void;
	openSnackbar: (snackbar: SnackbarInfo) => void;
}

const defaultContext: Context = {
	isOpen: false,
	closeSnackbar: noop,
	openSnackbar: noop,
};

const SnackbarContext = React.createContext(defaultContext);

const SnackbarContextProvider: React.FC = (props) => {
	const [snackbar, setSnackbar] = useState({
		type: undefined,
		isOpen: false,
		message: '',
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	const openSnackbar = useCallback(
		(snackbar) => {
			setSnackbar({
				...snackbar,
				isOpen: true,
			});
		},
		[setSnackbar]
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	const closeSnackbar = useCallback(() => {
		setSnackbar({
			type: undefined,
			message: '',
			isOpen: false,
		});
	}, [setSnackbar]);

	Object.assign(defaultContext, { closeSnackbar, openSnackbar });

	return (
		<SnackbarContext.Provider value={defaultContext}>
			{snackbar.type && (
				<AppSnackbar
					severity={snackbar.type}
					message={snackbar.message}
					isOpen={snackbar.isOpen}
					onCloseAlert={() => closeSnackbar()}
				/>
			)}
			{props.children}
		</SnackbarContext.Provider>
	);
};

export { SnackbarContext, SnackbarContextProvider };
